import React, { useEffect, useState } from 'react';
import {
  buildMedicationRedaction,
  buildPatientDetailLine,
  buildPatientIdentityBlock,
  buildPrescriptionMeta,
  buildPrescriptionRefParts,
  formatMedicationIndexName
} from '../../doctor/utils/prescriptionRedaction';
import {
  buildPrescriptionQrPayload,
  renderPrescriptionQrDataUrl
} from '../../doctor/utils/prescriptionQr';
import { formatPharmacyLineTotal, formatPharmacyPrice } from '../utils/dispensationPricing';
import '../../doctor/ConsultationPrescriptionViewModal.css';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo_vertical_noir.png`;
const FUNTOA_SLOGAN_LINES = ['Sécurité et santé :', 'main dans la main vers le succes'];
const FUNTOA_FOOTER_LINES = [
  'Agrément N° 221-14/MFPTLS/SG/DGTLS du 18/12/2014 – NIF 400 388 5426 – STAT 86909 31 2019 0 01640',
  'Téléphone : 032 07 366 11'
];

function formatDoctorName(name) {
  const n = (name || '').trim();
  if (!n) return null;
  return n.replace(/^Dr\.?\s*/i, '');
}

function mapItemsForRedaction(items = []) {
  return items.map((item) => ({
    ...item,
    dosage: item.dosage || item.dosage_instructions || ''
  }));
}

function MedicationList({ items = [], showLots = false, showPricing = false }) {
  if (!items.length) {
    return <p className="rx-premium__empty">—</p>;
  }

  return (
    <ol className="rx-premium__med-list">
      {items.map((item, index) => {
        const { name, qtyLine, detailLines } = buildMedicationRedaction(item);
        const extra = [...detailLines];
        if (showLots && item.lot_number) {
          const expiry = item.expiry_date
            ? ` · péremption ${String(item.expiry_date).slice(0, 10).split('-').reverse().join('/')}`
            : '';
          extra.push(`Lot délivré : ${item.lot_number}${expiry}`);
        }
        const priceLabel = showPricing ? formatPharmacyPrice(item.price) : null;
        const lineTotalLabel =
          showPricing && item.line_total != null ? formatPharmacyLineTotal(item.line_total) : null;
        const hasPosology = extra.length > 0;
        return (
          <li key={item.medication_id || item.id || index} className="rx-premium__med-item">
            <div className="rx-premium__med-head">
              <p className="rx-premium__med-name">{formatMedicationIndexName(index, name)}</p>
              {qtyLine ? <p className="rx-premium__med-qty">{qtyLine}</p> : null}
            </div>
            {hasPosology ? (
              <div className="rx-premium__med-poso">
                <span className="rx-premium__med-poso-label">Posologie</span>
                {extra.map((line, i) => (
                  <p key={i} className="rx-premium__med-line">
                    {line}
                  </p>
                ))}
              </div>
            ) : null}
            {priceLabel || lineTotalLabel ? (
              <p className="rx-premium__med-line rx-premium__med-line--price">
                {priceLabel ? `Prix unitaire : ${priceLabel}` : 'Prix unitaire : —'}
                {lineTotalLabel ? ` · ${lineTotalLabel}` : ''}
              </p>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Feuille ordonnance / dispensation (même rendu que la vue médecin).
 */
export default function PrescriptionDocumentSheet({
  consultation = null,
  prescription = null,
  mode = 'prescription',
  showLots = false,
  showPricing = false,
  pricing = null,
  totalPriceLabel = 'Total médicaments',
  partialPriceHint = null,
  sheetId = 'prescription-print-sheet',
  className = ''
}) {
  const patient = buildPatientIdentityBlock(consultation, prescription);
  const meta = buildPrescriptionMeta(consultation, prescription);
  const notes = (prescription?.notes || '').trim();
  const items = mapItemsForRedaction(prescription?.items || []);
  const doctorName = formatDoctorName(meta.doctorName);
  const patientDetailLine = buildPatientDetailLine(patient);
  const refParts = buildPrescriptionRefParts(meta);
  const isManual = mode === 'manual';
  const docTitle = isManual ? 'Dispensation' : 'Ordonnance';
  const ordonnanceNumber = !isManual ? refParts?.prescriptionNumber : null;

  const qrPayload = buildPrescriptionQrPayload({
    patientName: patient.name,
    companyName: patient.company,
    medications: items.map((item) => {
      const { name, qtyLine, detailLines } = buildMedicationRedaction(item);
      return {
        name,
        posology: detailLines[0] || item.dosage || '',
        quantity: qtyLine || ''
      };
    })
  });
  const [qrDataUrl, setQrDataUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!qrPayload) {
      setQrDataUrl(null);
      return undefined;
    }
    void renderPrescriptionQrDataUrl(qrPayload, { size: 128 }).then((url) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [qrPayload]);

  return (
    <article className={`rx-premium cp-rx-sheet${className ? ` ${className}` : ''}`} id={sheetId}>
      <div className="rx-premium__band" aria-hidden="true" />

      <header className="rx-premium__header">
        <div className="rx-premium__top">
          <img src={LOGO_SRC} alt="FUNTOA SMIE — Service Médical Interentreprise" className="rx-premium__logo" />
          <div className="rx-premium__brand-center">
            <p className="rx-premium__slogan">
              {FUNTOA_SLOGAN_LINES.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </p>
            <h1 className="rx-premium__doc-title">{docTitle}</h1>
          </div>
          <div className="rx-premium__site-block">
            {meta.siteName ? <p className="rx-premium__site-top">{meta.siteName}</p> : null}
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt={`QR ${docTitle.toLowerCase()} ${ordonnanceNumber || ''}`.trim()}
                className="rx-premium__qr"
                width={84}
                height={84}
              />
            ) : null}
          </div>
        </div>
        {ordonnanceNumber ? <p className="rx-premium__doc-number">{ordonnanceNumber}</p> : null}
        <div className="rx-premium__header-rule" aria-hidden="true" />
      </header>

      <div className="rx-premium__content">
        <div className="rx-premium__fields">
          <div className="rx-premium__field">
            <span className="rx-premium__field-label">Nom</span>
            <span className="rx-premium__field-value">{patient.name}</span>
          </div>
          {patientDetailLine ? (
            <div className="rx-premium__field">
              <span className="rx-premium__field-label">Infos</span>
              <span className="rx-premium__field-value rx-premium__field-value--meta">{patientDetailLine}</span>
            </div>
          ) : null}
          {refParts?.date ? (
            <div className="rx-premium__field">
              <span className="rx-premium__field-label">Date</span>
              <span className="rx-premium__field-value rx-premium__field-value--meta">{refParts.date}</span>
            </div>
          ) : null}
        </div>

        <MedicationList items={items} showLots={showLots} showPricing={showPricing} />

        {showPricing && pricing && pricing.total > 0 ? (
          <div className="rx-premium__pricing">
            <div className="rx-premium__pricing-row">
              <span className="rx-premium__pricing-label">{totalPriceLabel}</span>
              <strong className="rx-premium__pricing-value">{formatPharmacyLineTotal(pricing.total)}</strong>
            </div>
            {!pricing.complete && partialPriceHint ? (
              <p className="rx-premium__pricing-hint">{partialPriceHint}</p>
            ) : null}
          </div>
        ) : null}

        {notes ? (
          <div className="rx-premium__notes">
            <span className="rx-premium__notes-label">Notes</span>
            <p className="rx-premium__notes-body">{notes}</p>
          </div>
        ) : null}
      </div>

      <footer className="rx-premium__footer">
        <div className="rx-premium__sign">
          {isManual ? (
            <>
              <p className="rx-premium__stamp-label">Signature</p>
              <div className="rx-premium__stamp" aria-hidden="true" />
              <p className="rx-premium__doctor">Validation pharmacien</p>
            </>
          ) : (
            <>
              <p className="rx-premium__stamp-label">Signature</p>
              <div className="rx-premium__stamp" aria-hidden="true" />
              {doctorName ? <p className="rx-premium__doctor">Dr {doctorName}</p> : null}
            </>
          )}
        </div>

        <div className="rx-premium__band rx-premium__band--footer" aria-hidden="true" />
        <div className="rx-premium__legal">
          {FUNTOA_FOOTER_LINES.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </footer>
    </article>
  );
}

export function dispensationToPrescriptionView(detail) {
  if (!detail) return { consultation: null, prescription: null };
  const items = (detail.items || []).map((it) => ({
    id: it.id,
    medication_id: it.medication_id,
    medication_name: it.medication_name,
    unit_name: it.unit_name,
    quantity: it.quantity,
    dosage_instructions: it.dosage_instructions,
    lot_number: it.lot_number,
    expiry_date: it.expiry_date,
    price: it.price,
    line_total: it.line_total
  }));

  const prescription = {
    patient_name: detail.patient_name,
    member_company_name: detail.member_company_name || detail.company_name || null,
    notes: detail.prescription_notes || detail.notes || '',
    items,
    site_id: detail.site_id,
    site_name: detail.site_name,
    prescribed_date: detail.prescription_date || detail.created_at,
    consultation_id: detail.consultation_id,
    id: detail.medical_prescription_id,
    doctor_name: detail.prescriber_name
  };

  const consultation = {
    id: detail.consultation_id,
    patient_name: detail.patient_name,
    member_company_name: detail.member_company_name || detail.company_name || null,
    site_id: detail.site_id,
    site_name: detail.site_name
  };

  return { consultation, prescription };
}
